/* =========================================================================
   BetterVits Quiz — quiz.js  (vanilla, no dependencies)
   =========================================================================
   Edit only these two blocks for normal maintenance:
     1) TIER_PRICES  — display prices per pricing_tier (must match the Function)
     2) GOALS / SYMPTOM_MAP — the quiz question content
   Everything below "ENGINE" rarely needs touching.
   ========================================================================= */

/* ----------------------------------------------------------------------- */
/* 1) TIER PRICE TABLE — decoded from the BVUK Shopify Function so the      */
/*    quiz shows exactly what checkout charges. Keys match the             */
/*    custom.pricing_tier metafield. Values are PER-PACK TOTAL prices (£).  */
/*                                                                          */
/*    `null` means the Function does NOT discount that combination, so the */
/*    real price is the product's own base price (basePrice from Liquid).  */
/*    quiz.js substitutes basePrice * qty automatically when it sees null. */
/*                                                                          */
/*    If you change a tier in the Function, change it here too.            */
/* ----------------------------------------------------------------------- */
const TIER_PRICES = {
  // qty1 OTP = base variant price (Function leaves qty1 OTP untouched)
  "bvuk-tier-1":  { otp: { 1: null, 3: 80.00,  6: 150.00 }, sub: { 1: 35.00, 2: 55.00, 3: 75.00 } },
  "bvuk-tier-2":  { otp: { 1: null, 3: 80.00,  6: 150.00 }, sub: { 1: 35.00, 2: 55.00, 3: 75.00 } },
  "bvuk-tier-3":  { otp: { 1: null, 3: 75.00,  6: 135.00 }, sub: { 1: 25.00, 2: 45.00, 3: 60.00 } },
  "bvuk-tier-4":  { otp: { 1: null, 3: 80.00,  6: 150.00 }, sub: { 1: 30.00, 2: 55.00, 3: 75.00 } },
  "bvuk-tier-5":  { otp: { 1: null, 3: 60.00,  6: 100.00 }, sub: { 1: 23.00, 2: 40.00, 3: 55.00 } },
  "bvuk-tier-6":  { otp: { 1: 35.00, 3: 82.50, 6: 150.00 }, sub: { 1: 30.00, 2: 55.00, 3: 75.00 } },
  "bvuk-tier-7":  { otp: { 1: null, 3: 90.00,  6: 150.00 }, sub: { 1: 30.00, 2: 55.00, 3: 75.00 } },
  "bvuk-tier-8":  { otp: { 1: null, 3: 105.00, 6: 180.00 }, sub: { 1: 35.00, 2: 65.00, 3: 90.00 } },
  "bvuk-tier-9":  { otp: { 1: null, 3: 120.00, 6: 210.00 }, sub: { 1: null,  2: 70.00, 3: 90.00 } },
  "bvuk-tier-10": { otp: { 1: null, 3: 150.00, 6: 270.00 }, sub: { 1: null,  2: 95.00, 3: 135.00 } },
  "bvuk-tier-11": { otp: { 1: null, 3: 80.00,  6: 150.00 }, sub: { 1: 27.50, 2: 55.00, 3: 75.00 } },
};

/* NOTE on bvuk-tier-1 (Berberine): the Function applies a RELATIVE discount
   for OTP qty 4–5 (not a fixed price), so only the 1/3/6 tiers shown in the
   quiz are exact fixed prices. Since the quiz only offers 1/3/6, this is fine. */

/* Quantities offered per mode. OTP shows 1/3/6, SUB shows 1/2/3 (per Function). */
const QTY_OPTIONS = { otp: [1, 3, 6], sub: [1, 2, 3] };

/* ----------------------------------------------------------------------- */
/* 2) QUESTION CONTENT                                                     */
/*    GOALS drive step "what do you want to improve".                      */
/*    SYMPTOM_MAP[goalId] = symptoms shown when that goal is picked.       */
/*    These IDs must match the values you put in the product metafields    */
/*    custom.quiz_goals and custom.quiz_symptoms.                          */
/* ----------------------------------------------------------------------- */
const GOALS = [
  { id: "cognition", label: "Cognition / Brain" },
  { id: "gut",       label: "Gut Health" },
  { id: "joints",    label: "Hips, Joints & Knees" },
  { id: "mental",    label: "Mental Health" },
  { id: "hair_skin", label: "Hair, Skin & Nails" },
  { id: "immunity",  label: "Immunity" },
  { id: "pms",       label: "PMS" },
  { id: "heart",     label: "Heart Health" },
  { id: "menopause", label: "Menopause" },
  { id: "weight",    label: "Weight Loss" },
  { id: "sleep",     label: "Sleep" },
];

const SYMPTOM_MAP = {
  cognition: ["brain_fog", "headaches", "low_energy", "fatigue"],
  gut:       ["constipation", "diarrhea", "bloating", "ibs"],
  joints:    ["arthritis", "inflammation", "cramps"],
  mental:    ["anxiety_depression", "restlessness", "low_energy"],
  hair_skin: ["skin_issues", "brittle_nails", "acne", "eczema", "wrinkles"],
  immunity:  ["weak_immunity", "low_energy"],
  pms:       ["pms", "cramps", "anxiety_depression"],
  heart:     ["heart_palpitations", "poor_heart_health"],
  menopause: ["menopause", "sleeplessness", "anxiety_depression"],
  weight:    ["overweight", "low_energy", "bloating"],
  sleep:     ["sleeplessness", "insomnia", "restlessness"],
};

/* Human-readable labels for symptom IDs (used in UI + summary). */
const SYMPTOM_LABELS = {
  brain_fog: "Brain Fog", anxiety_depression: "Anxiety or Depression",
  headaches: "Headaches", weak_immunity: "Weak Immunity", low_energy: "Low Energy",
  constipation: "Constipation", diarrhea: "Diarrhea", cramps: "Cramps",
  skin_issues: "Skin Issues", brittle_nails: "Brittle Nails",
  sleeplessness: "Sleeplessness", bloating: "Bloating or Gas",
  arthritis: "Arthritis", inflammation: "Inflammation", menopause: "Menopause",
  heart_palpitations: "Heart Palpitations", poor_heart_health: "Poor Heart Health",
  acne: "Acne", eczema: "Eczema", wrinkles: "Wrinkles", pms: "PMS", ibs: "IBS",
  fatigue: "Fatigue", insomnia: "Insomnia", restlessness: "Restlessness",
  overweight: "Overweight",
};

const DIETS = ["Vegetarian", "Vegan", "Dairy Free", "Gluten Free", "Soy Free", "None of the Above"];
const ALLERGIES = ["Milk", "Nuts", "Soy", "Citrus", "Fish", "Sugar", "Eggs", "None of the Above"];

/* ----------------------------------------------------------------------- */
/* FIREBASE — Firestore REST write (create-only; secured by rules).        */
/* Fill in your projectId. No secret key in the client; access is          */
/* controlled entirely by Firestore security rules (see the .rules file).  */
/* ----------------------------------------------------------------------- */
const FIREBASE = {
  projectId: "YOUR_FIREBASE_PROJECT_ID",
  collection: "quiz_submissions",
};

/* ============================ ENGINE ==================================== */

const QUIZ = {
  products: [],
  sellingPlans: {},
  currency: "GBP",
  answers: {
    firstName: "", lastName: "", email: "", dob: "",
    goals: [], symptoms: [], medication: null,
    diet: [], allergies: [], wantsCall: null,
  },
  step: 0,
};

function money(n) {
  const sym = QUIZ.currency === "GBP" ? "£" : "";
  return sym + Number(n).toFixed(2);
}

function loadData() {
  try {
    QUIZ.products = JSON.parse(document.getElementById("bv-quiz-data").textContent) || [];
  } catch (e) { QUIZ.products = []; }
  try {
    QUIZ.sellingPlans = JSON.parse(document.getElementById("bv-quiz-selling-plans").textContent) || {};
  } catch (e) { QUIZ.sellingPlans = {}; }
  QUIZ.currency = document.getElementById("bv-quiz-root").dataset.shopCurrency || "GBP";

  // Normalise metafield lists (Shopify list metafields arrive as arrays already
  // via .value, but guard against a single string just in case).
  QUIZ.products.forEach(p => {
    p.goals = Array.isArray(p.goals) ? p.goals : (p.goals ? [p.goals] : []);
    p.symptoms = Array.isArray(p.symptoms) ? p.symptoms : (p.symptoms ? [p.symptoms] : []);
    p.media = Array.isArray(p.media) ? p.media.filter(m => m && m.src) : [];
    // Fallback: if no media array, use featured image as a single slide
    if (p.media.length === 0 && p.image) p.media = [{ type: "image", src: p.image, alt: p.title }];
  });
}

/* ---- Matching: score products against the user's goals + symptoms ---- */
function matchProducts() {
  const userGoals = new Set(QUIZ.answers.goals);
  const userSymptoms = new Set(QUIZ.answers.symptoms);

  const scored = QUIZ.products
    .filter(p => p.available && p.pricingTier) // must be buyable + priced
    .map(p => {
      let score = 0;
      p.goals.forEach(g => { if (userGoals.has(g)) score += 2; });   // goal match weighted higher
      p.symptoms.forEach(s => { if (userSymptoms.has(s)) score += 1; });
      return { product: p, score };
    })
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, 2).map(x => x.product); // top 1–2
}

/* ---- Render helpers for each step ---- */
function el(tag, attrs = {}, html = "") {
  const node = document.createElement(tag);
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === "class") node.className = v;
    else if (k.startsWith("on") && typeof v === "function") node.addEventListener(k.slice(2), v);
    else node.setAttribute(k, v);
  });
  if (html) node.innerHTML = html;
  return node;
}

const STEPS = [
  renderIntro, renderDetails, renderGoals, renderSymptoms,
  renderMedication, renderDiet, renderAllergies, renderCall, renderResults,
];

function go(n) {
  QUIZ.step = Math.max(0, Math.min(STEPS.length - 1, n));
  render();
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function render() {
  const root = document.getElementById("bv-quiz-root");
  root.innerHTML = "";

  const progress = el("div", { class: "bvq-progress" });
  const pct = (QUIZ.step / (STEPS.length - 1)) * 100;
  progress.appendChild(el("div", { class: "bvq-progress-bar", style: `width:${pct}%` }));
  root.appendChild(progress);

  const card = el("div", { class: "bvq-card" });
  STEPS[QUIZ.step](card);
  root.appendChild(card);
}

function navButtons(card, { next = true, back = true, nextLabel = "Next", onNext } = {}) {
  const row = el("div", { class: "bvq-nav" });
  if (back && QUIZ.step > 0) {
    row.appendChild(el("button", { class: "bvq-btn bvq-btn-ghost", onclick: () => go(QUIZ.step - 1) }, "Back"));
  }
  if (next) {
    row.appendChild(el("button", {
      class: "bvq-btn bvq-btn-primary",
      onclick: () => { if (!onNext || onNext() !== false) go(QUIZ.step + 1); }
    }, nextLabel));
  }
  card.appendChild(row);
}

/* ---- Multi-select chip group ---- */
function chipGroup(items, selectedArr, { single = false } = {}) {
  const wrap = el("div", { class: "bvq-chips" });
  items.forEach(item => {
    const id = item.id || item;
    const label = item.label || item;
    const chip = el("button", { class: "bvq-chip", type: "button" }, label);
    if (selectedArr.includes(id)) chip.classList.add("is-selected");
    chip.addEventListener("click", () => {
      const i = selectedArr.indexOf(id);
      if (single) { selectedArr.length = 0; if (i === -1) selectedArr.push(id); }
      else { i === -1 ? selectedArr.push(id) : selectedArr.splice(i, 1); }
      wrap.querySelectorAll(".bvq-chip").forEach((c, idx) => {
        const cid = (items[idx].id || items[idx]);
        c.classList.toggle("is-selected", selectedArr.includes(cid));
      });
    });
    wrap.appendChild(chip);
  });
  return wrap;
}

/* =============================== STEPS ================================= */

function renderIntro(card) {
  card.appendChild(el("p", { class: "bvq-kicker" }, "Quick Quiz"));
  card.appendChild(el("h1", { class: "bvq-h1" }, "Stop guessing your way to good health"));
  card.appendChild(el("p", { class: "bvq-lead" }, "Superior health kicks off with our two-minute quiz. Finish it for 10% off."));
  navButtons(card, { back: false, nextLabel: "Get started" });
}

function renderDetails(card) {
  card.appendChild(el("h2", { class: "bvq-h2" }, "Let's get to know you"));
  const a = QUIZ.answers;
  const grid = el("div", { class: "bvq-form" });

  const fName = el("input", { class: "bvq-input", type: "text", placeholder: "First name", value: a.firstName });
  const lName = el("input", { class: "bvq-input", type: "text", placeholder: "Surname", value: a.lastName });
  const email = el("input", { class: "bvq-input", type: "email", placeholder: "Email address", value: a.email });
  const dob   = el("input", { class: "bvq-input", type: "date", value: a.dob });

  [fName, lName, email, dob].forEach(i => grid.appendChild(i));
  card.appendChild(grid);
  const err = el("p", { class: "bvq-error" }); card.appendChild(err);

  navButtons(card, {
    onNext: () => {
      a.firstName = fName.value.trim(); a.lastName = lName.value.trim();
      a.email = email.value.trim(); a.dob = dob.value;
      if (!a.firstName || !a.email || !/.+@.+\..+/.test(a.email)) {
        err.textContent = "Please enter your name and a valid email."; return false;
      }
    }
  });
}

function renderGoals(card) {
  card.appendChild(el("h2", { class: "bvq-h2" }, "What are you looking to improve?"));
  card.appendChild(chipGroup(GOALS, QUIZ.answers.goals));
  const err = el("p", { class: "bvq-error" }); card.appendChild(err);
  navButtons(card, {
    onNext: () => {
      if (QUIZ.answers.goals.length === 0) { err.textContent = "Pick at least one."; return false; }
      // Prune symptoms that no longer belong to any selected goal
      const allowed = new Set();
      QUIZ.answers.goals.forEach(g => (SYMPTOM_MAP[g] || []).forEach(s => allowed.add(s)));
      QUIZ.answers.symptoms = QUIZ.answers.symptoms.filter(s => allowed.has(s));
    }
  });
}

function renderSymptoms(card) {
  card.appendChild(el("h2", { class: "bvq-h2" }, "Which symptoms are you facing?"));
  // Build symptom list from selected goals (deduped, order preserved)
  const seen = new Set(); const symptoms = [];
  QUIZ.answers.goals.forEach(g => (SYMPTOM_MAP[g] || []).forEach(s => {
    if (!seen.has(s)) { seen.add(s); symptoms.push({ id: s, label: SYMPTOM_LABELS[s] || s }); }
  }));
  if (symptoms.length === 0) {
    card.appendChild(el("p", { class: "bvq-lead" }, "No specific symptoms to narrow down — tap Next."));
  } else {
    card.appendChild(chipGroup(symptoms, QUIZ.answers.symptoms));
  }
  navButtons(card);
}

function renderMedication(card) {
  card.appendChild(el("h2", { class: "bvq-h2" }, "Do you currently take any medication for your symptoms?"));
  card.appendChild(chipGroup(["Yes", "No"], boolProxy("medication"), { single: true }));
  navButtons(card);
}

function renderDiet(card) {
  card.appendChild(el("h2", { class: "bvq-h2" }, "Choose your diet"));
  card.appendChild(chipGroup(DIETS, QUIZ.answers.diet));
  navButtons(card);
}

function renderAllergies(card) {
  card.appendChild(el("h2", { class: "bvq-h2" }, "Do you have any allergies?"));
  card.appendChild(chipGroup(ALLERGIES, QUIZ.answers.allergies));
  navButtons(card);
}

function renderCall(card) {
  card.appendChild(el("h2", { class: "bvq-h2" }, "Would you like to arrange a call with a nutrition expert?"));
  card.appendChild(chipGroup(["Yes", "No"], boolProxy("wantsCall"), { single: true }));
  card.appendChild(el("p", { class: "bvq-fine" },
    'By proceeding you agree to our <a href="/pages/terms">terms and conditions</a>.'));
  navButtons(card, {
    nextLabel: "Get my results",
    onNext: () => { submitToFirebase(); } // fire-and-forget; results render regardless
  });
}

/* A tiny adapter so Yes/No single-selects map onto a boolean answer. */
function boolProxy(key) {
  const arr = [];
  if (QUIZ.answers[key] === true) arr.push("Yes");
  if (QUIZ.answers[key] === false) arr.push("No");
  return new Proxy(arr, {
    set(t, prop, val) {
      t[prop] = val;
      QUIZ.answers[key] = t.includes("Yes") ? true : t.includes("No") ? false : null;
      return true;
    }
  });
}

/* =============================== RESULTS =============================== */

function renderResults(card) {
  const a = QUIZ.answers;
  card.appendChild(el("h2", { class: "bvq-h2" }, `Here's your plan, ${escapeHtml(a.firstName)}`));
  card.appendChild(el("p", { class: "bvq-lead" }, "Based on your answers, we recommend:"));

  // Summary
  const sum = el("div", { class: "bvq-summary" });
  sum.innerHTML = `
    <div><span>Birthday</span><strong>${a.dob || "—"}</strong></div>
    <div><span>Diet</span><strong>${a.diet.join(", ") || "—"}</strong></div>
    <div><span>Symptoms</span><strong>${a.symptoms.map(s => SYMPTOM_LABELS[s] || s).join(", ") || "—"}</strong></div>
    <div><span>Allergies</span><strong>${a.allergies.join(", ") || "—"}</strong></div>`;
  card.appendChild(sum);

  const matches = matchProducts();
  if (matches.length === 0) {
    card.appendChild(el("p", { class: "bvq-lead" }, "We couldn't find a precise match — explore our full range."));
    card.appendChild(el("a", { class: "bvq-btn bvq-btn-primary", href: "/collections/all" }, "Shop all"));
    return;
  }

  matches.forEach(p => card.appendChild(productCard(p)));
}

/* Swipeable media carousel: images + video, dots, arrows, touch drag. */
function buildCarousel(p) {
  const media = p.media && p.media.length ? p.media : [];
  const root = el("div", { class: "bvq-carousel" });
  if (media.length === 0) return root;

  const track = el("div", { class: "bvq-carousel-track" });
  media.forEach(m => {
    const slide = el("div", { class: "bvq-slide" });
    if (m.type === "video") {
      const v = el("video", { class: "bvq-slide-media", controls: "", preload: "metadata" });
      if (m.poster) v.setAttribute("poster", m.poster);
      v.appendChild(el("source", { src: m.src, type: "video/mp4" }));
      slide.appendChild(v);
    } else if (m.type === "external_video") {
      const f = el("iframe", { class: "bvq-slide-media", src: m.src, frameborder: "0",
        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
        allowfullscreen: "" });
      slide.appendChild(f);
    } else {
      slide.appendChild(el("img", { class: "bvq-slide-media", src: m.src, alt: m.alt || p.title, loading: "lazy" }));
    }
    track.appendChild(slide);
  });
  root.appendChild(track);

  let index = 0;
  const go2 = (i) => {
    index = Math.max(0, Math.min(media.length - 1, i));
    track.style.transform = `translateX(-${index * 100}%)`;
    dots.querySelectorAll("button").forEach((d, di) => d.classList.toggle("is-active", di === index));
  };

  if (media.length > 1) {
    const prev = el("button", { class: "bvq-carousel-arrow bvq-prev", type: "button", "aria-label": "Previous" }, "‹");
    const next = el("button", { class: "bvq-carousel-arrow bvq-next", type: "button", "aria-label": "Next" }, "›");
    prev.addEventListener("click", () => go2(index - 1));
    next.addEventListener("click", () => go2(index + 1));
    root.append(prev, next);
  }

  const dots = el("div", { class: "bvq-carousel-dots" });
  media.forEach((_, i) => {
    const d = el("button", { class: "bvq-dot" + (i === 0 ? " is-active" : ""), type: "button", "aria-label": `Slide ${i + 1}` });
    d.addEventListener("click", () => go2(i));
    dots.appendChild(d);
  });
  if (media.length > 1) root.appendChild(dots);

  // Touch / drag swipe
  let startX = 0, dragging = false;
  const onStart = (x) => { startX = x; dragging = true; };
  const onEnd = (x) => {
    if (!dragging) return; dragging = false;
    const dx = x - startX;
    if (Math.abs(dx) > 40) go2(index + (dx < 0 ? 1 : -1));
  };
  track.addEventListener("touchstart", e => onStart(e.touches[0].clientX), { passive: true });
  track.addEventListener("touchend", e => onEnd(e.changedTouches[0].clientX));
  track.addEventListener("mousedown", e => {
    onStart(e.clientX);
    // Attach a one-shot mouseup so we never leak listeners on window.
    const up = (ev) => { document.removeEventListener("mouseup", up); onEnd(ev.clientX); };
    document.addEventListener("mouseup", up);
  });

  return root;
}

function productCard(p) {
  const wrap = el("div", { class: "bvq-product" });
  wrap.appendChild(el("p", { class: "bvq-kicker" }, "We recommend"));

  // Media carousel (images + video)
  wrap.appendChild(buildCarousel(p));

  const headText = el("div", { class: "bvq-product-head" });
  headText.appendChild(el("h3", { class: "bvq-h3" }, escapeHtml(p.title)));
  headText.appendChild(el("p", { class: "bvq-desc" }, escapeHtml(p.description || "")));
  wrap.appendChild(headText);

  // "Helps with" chips — matched ones highlighted
  const matchedSet = new Set(QUIZ.answers.symptoms);
  if (p.symptoms.length) {
    const helps = el("div", { class: "bvq-helps" });
    helps.appendChild(el("span", { class: "bvq-helps-label" }, "Helps with"));
    const chips = el("div", { class: "bvq-helps-chips" });
    p.symptoms.forEach(s => {
      const chip = el("span", {
        class: "bvq-helps-chip" + (matchedSet.has(s) ? " is-match" : "")
      }, escapeHtml(SYMPTOM_LABELS[s] || s));
      chips.appendChild(chip);
    });
    helps.appendChild(chips);
    wrap.appendChild(helps);
  }

  // Mode toggle
  const state = { mode: "otp", qty: QTY_OPTIONS.otp[0] };
  const tier = TIER_PRICES[p.pricingTier];

  const toggle = el("div", { class: "bvq-toggle" });
  const otpBtn = el("button", { class: "bvq-toggle-btn is-active", type: "button" }, "One-time");
  const subBtn = el("button", { class: "bvq-toggle-btn", type: "button" }, "Subscribe & Save");
  toggle.append(otpBtn, subBtn);
  wrap.appendChild(toggle);

  const qtyWrap = el("div", { class: "bvq-qtys" });
  wrap.appendChild(qtyWrap);

  function paintQtys() {
    qtyWrap.innerHTML = "";
    const opts = QTY_OPTIONS[state.mode];
    if (!opts.includes(state.qty)) state.qty = opts[0];
    opts.forEach(q => {
      let price = tier ? tier[state.mode][q] : null;
      // null = Function doesn't discount this combo -> use the correct base price.
      // OTP null -> variant base price; SUB null -> sub-adjusted base (falls back
      // to variant price only if no selling-plan allocation was emitted).
      if (price == null) {
        const unit = state.mode === "sub"
          ? (p.subBasePrice != null ? Number(p.subBasePrice) : Number(p.basePrice) || 0)
          : (Number(p.basePrice) || 0);
        price = unit * q;
      }
      const label = state.mode === "sub"
        ? `${q} ${q === 1 ? "bottle" : "bottles"} / delivery`
        : `${q} ${q === 1 ? "bottle" : "bottles"}`;
      const each = q > 1 ? `${money(price / q)} each` : "";
      const row = el("button", { class: "bvq-qty" + (q === state.qty ? " is-selected" : ""), type: "button" });
      row.innerHTML = `<span class="bvq-qty-n">${label}</span>
                       <span class="bvq-qty-p">${money(price)}</span>
                       <span class="bvq-qty-e">${each}</span>`;
      row.addEventListener("click", () => { state.qty = q; paintQtys(); });
      qtyWrap.appendChild(row);
    });
  }
  function setMode(m) {
    state.mode = m;
    otpBtn.classList.toggle("is-active", m === "otp");
    subBtn.classList.toggle("is-active", m === "sub");
    paintQtys();
  }
  otpBtn.addEventListener("click", () => setMode("otp"));
  subBtn.addEventListener("click", () => setMode("sub"));
  paintQtys();

  const atc = el("button", { class: "bvq-btn bvq-btn-primary bvq-atc", type: "button" }, "Add to cart");
  atc.addEventListener("click", () => addToCart(p, state, atc));
  wrap.appendChild(atc);

  if (!tier) {
    wrap.appendChild(el("p", { class: "bvq-error" },
      `No price tier set for "${escapeHtml(p.title)}" (metafield custom.pricing_tier).`));
  }
  return wrap;
}

/* =============================== CART ================================= */

async function addToCart(p, state, btn) {
  if (!p.variantId) {
    alert("Sorry, this product is currently unavailable.");
    return;
  }
  const original = btn.textContent;
  btn.disabled = true; btn.textContent = "Adding…";

  // SUB qty maps directly to units; OTP qty is bottle count = units.
  const quantity = state.qty;
  const item = { id: p.variantId, quantity };

  if (state.mode === "sub") {
    const planId = QUIZ.sellingPlans[String(p.id)] || QUIZ.sellingPlans[p.id];
    if (planId) item.selling_plan = planId;
  }

  try {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [item] }),
    });
    if (!res.ok) throw new Error("cart");
    btn.textContent = "Added ✓";
    setTimeout(() => { window.location.href = "/cart"; }, 600);
  } catch (e) {
    btn.disabled = false; btn.textContent = original;
    alert("Sorry, something went wrong adding to cart. Please try again.");
  }
}

/* ============================== FIREBASE ============================== */
/* Writes one document per submission via the Firestore REST API.
   Security is enforced by Firestore rules (create-only, schema-checked).  */

function submitToFirebase() {
  const a = QUIZ.answers;
  const url = `https://firestore.googleapis.com/v1/projects/${FIREBASE.projectId}` +
              `/databases/(default)/documents/${FIREBASE.collection}`;
  const doc = {
    fields: {
      firstName: { stringValue: a.firstName },
      lastName:  { stringValue: a.lastName },
      email:     { stringValue: a.email },
      dob:       { stringValue: a.dob || "" },
      goals:     { arrayValue: { values: a.goals.map(v => ({ stringValue: v })) } },
      symptoms:  { arrayValue: { values: a.symptoms.map(v => ({ stringValue: v })) } },
      medication:{ booleanValue: !!a.medication },
      diet:      { arrayValue: { values: a.diet.map(v => ({ stringValue: v })) } },
      allergies: { arrayValue: { values: a.allergies.map(v => ({ stringValue: v })) } },
      wantsCall: { booleanValue: !!a.wantsCall },
      createdAt: { timestampValue: new Date().toISOString() },
    }
  };
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(doc),
  }).catch(() => { /* non-blocking: never stop the user seeing results */ });
}

/* =============================== UTIL ================================= */
function escapeHtml(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

document.addEventListener("DOMContentLoaded", () => { loadData(); go(0); });
